
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import Banner from "../Components/Banner";

const servicesData = [
  {
    id: 1,
    title: "Product Engineering",
    desc: "Design, develop, and evolve digital products with scalable architectures, modern technologies, and user-focused engineering that accelerates innovation.",
    link: "/product-engineering",
    image: "/assets/services/img1.png",
  },
  {
    id: 2,
    title: "Performance Engineering",
    desc: "Improve application speed, reliability, scalability, and responsiveness through performance testing, optimization, and continuous engineering.",
    link: "/performance-engineering",
    image: "/assets/services/img2.png",
  },
  {
    id: 3,
    title: "Cloud Engineering",
    desc: "Build, migrate, and optimize cloud environments with secure architectures, automated infrastructure, and scalable cloud-native solutions.",
    link: "/cloud-engineering",
    image: "/assets/services/img3.png",
  },
  {
    id: 4,
    title: "Global Capability Centers",
    desc: "Establish and scale global capability centers with the right technology teams, operating models, and engineering capabilities to support business growth.",
    link: "/global-capability-centers",
    image: "/assets/services/img4.png",
  },
  {
    id: 5,
    title: "Industrial Automation",
    desc: "Streamline industrial operations with intelligent automation, connected systems, and optimized processes that improve productivity and efficiency.",
    link: "/industrial-automation",
    image: "/assets/services/img5.png",
  },
  {
    id: 6,
    title: "Embedded Services",
    desc: "Develop and integrate embedded solutions through hardware-software engineering, firmware development, and connected device technologies.",
    link: "/embedded-services",
    image: "/assets/services/img6.png",
  },
  {
    id: 7,
    title: "Staff Augmentation",
    desc: "Extend your engineering capabilities with skilled professionals who integrate into your teams and support evolving project requirements.",
    link: "/staff-augmentation",
    image: "/assets/services/img7.png",
  },
];

const DigitalEngineering = () => {
  return (
    <>
      <Helmet>
        <title>Digital Engineering Services | Mobikode</title>

        <meta
          name="description"
          content="Explore Mobikode's digital engineering services, including product engineering, performance engineering, cloud engineering, global capability centers, industrial automation, embedded services, and staff augmentation."
        />

        <meta
          name="keywords"
          content="Digital Engineering Services, Product Engineering, Performance Engineering, Cloud Engineering, Global Capability Centers, Industrial Automation, Embedded Services, Engineering Staff Augmentation"
        />

        <meta
          property="og:title"
          content="Digital Engineering Services | Mobikode"
        />

        <meta
          property="og:description"
          content="Build and run digital solutions with product engineering, cloud engineering, industrial automation, embedded services, and scalable engineering teams."
        />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Mobikode" />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Digital Engineering Services | Mobikode"
        />

        <meta
          name="twitter:description"
          content="Discover digital engineering capabilities that help businesses build, optimize, and scale technology solutions."
        />
      </Helmet>

      <div className="bg-[#FFFFFF]">
        {/* Hero Section */}
        <section className="relative w-full h-[500px] lg:h-[630px] flex items-center bg-[#000814] overflow-hidden py-12 md:py-0">
          <div className="absolute inset-0 z-0">
            <img
              src="/assets/services/bg3.png"
              alt="Digital engineering and technology solutions"
              className="w-full h-full object-cover"
            />

            <div
              style={{
                background:
                  "linear-gradient(86.84deg, #000000 20%, rgba(0, 0, 0, 0) 79%)",
              }}
              className="hidden md:block absolute inset-0"
            />

            <div
              style={{
                background:
                  "linear-gradient(0deg, #000000 40%, rgba(0, 0, 0, 0.3) 100%)",
              }}
              className="block md:hidden absolute inset-0"
            />
          </div>

          <div className="relative z-10 w-full max-w-[1440px] mx-auto px-[20px] md:px-[40px] lg:px-[80px] flex flex-col items-start justify-center h-full">
            <div className="w-full max-w-[500px] md:max-w-[700px] lg:max-w-[900px] mt-auto md:mt-0">
              <h1 className="text-white font-[600] text-[28px] sm:text-[36px] md:text-[50px] lg:text-[60px] font-sora leading-[1.2] lg:leading-[1.1] mb-[12px] lg:mb-[22px] drop-shadow-xl tracking-wide text-left">
                Digital Engineering
              </h1>

              <p className="text-[#FFFFFF] max-w-[800px] w-full text-[14px] sm:text-[16px] md:text-[18px] leading-[20px] md:leading-[22px] lg:leading-[24px] font-graphik font-light tracking-wider text-left opacity-90 lg:opacity-100">
                Allied services to build and run
              </p>
            </div>
          </div>
        </section>

        {/* Digital Engineering Services */}
        <section className="lg:py-[100px] py-[60px]">
          <div className="max-w-[1440px] w-full mx-auto lg:px-[80px] px-[20px]">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-[20px]">
              {servicesData.map((service) => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          </div>
        </section>

        {/* Closing Banner */}
        <Banner
          title="Engineering What's Next"
          text="Build, optimize, and scale your digital capabilities with engineering services designed to support innovation, operational excellence, and sustainable business growth."
        />
      </div>
    </>
  );
};

export default DigitalEngineering;

const ServiceCard = ({ service }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      animate={{ y: isHovered ? -15 : 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      <Link
        to={service.link}
        aria-label={`Explore ${service.title}`}
        className="group outline-none relative block w-full h-[290px] rounded-[0px] overflow-hidden bg-[#ebeff3] transition-colors duration-500 hover:bg-[#01386A] focus:bg-[#01386A] active:bg-[#01386A] cursor-pointer"
      >
        {/* Right Background Image */}
        <div className="absolute top-0 right-[-5%] sm:right-0 w-[45%] sm:w-[40%] h-full pointer-events-none overflow-hidden flex items-center justify-end">
          <img
            src={service.image}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-contain object-right opacity-90 group-hover:opacity-20 group-focus:opacity-20 group-active:opacity-20 transition-all duration-500 transform group-hover:scale-105"
          />
        </div>

        {/* Card Content */}
        <div className="relative z-10 flex flex-col justify-between h-full w-[85%] sm:w-[75%] p-8 lg:p-8">
          <h3 className="text-[16px] lg:text-[20px] font-[600] font-sora text-[#01386A] leading-[1.2] transition-colors duration-500 group-hover:text-white group-focus:text-white group-active:text-white">
            {service.title}
          </h3>

          <p className="absolute top-[105px] sm:top-[115px] pr-5 text-[14px] lg:text-[16px] font-[500] leading-[1.6] font-graphik text-gray-200 opacity-0 translate-y-4 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0 group-focus:opacity-100 group-focus:translate-y-0 group-active:opacity-100 group-active:translate-y-0">
            {service.desc}
          </p>

          <div className="mt-auto flex items-center gap-2 text-[15px] font-sora font-[600] text-[#01386A] transition-opacity duration-300 group-hover:opacity-0 group-focus:opacity-0 group-active:opacity-0">
            <span>Read More</span>
            <ArrowUpRight size={18} className="text-[#ED6800]" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
